import React, { useMemo } from 'react';
import { View } from 'react-native';
import { endOfMonth, format, startOfMonth } from 'date-fns';

import { useExpenses, useExpensesTotal } from '@/hooks/use-expenses';
import { EXPENSE_CATEGORY_LABELS } from '@/types/expense';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { SummaryEmpty, SummaryRow, SummarySkeleton, SummaryStat } from '../home-summary-dialog';

/** Prévia de Gastos: total do mês, maior categoria e os últimos lançamentos. */
export function ExpensesSummary() {
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    return {
      startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(now), 'yyyy-MM-dd'),
    };
  }, []);

  const { data: total } = useExpensesTotal(startDate, endDate);
  const { data: paginated, isLoading } = useExpenses({ startDate, endDate, page: 1, limit: 20 });

  const expenses = useMemo(() => paginated?.data ?? [], [paginated]);

  const topCategory = useMemo(() => {
    const totals = new Map<string, number>();
    expenses.forEach((expense) => {
      const key = expense.category ?? 'OTHER';
      totals.set(key, (totals.get(key) ?? 0) + expense.amount);
    });
    const [category] = [...totals.entries()].sort((a, b) => b[1] - a[1]);
    return category
      ? {
          label: EXPENSE_CATEGORY_LABELS[category[0] as keyof typeof EXPENSE_CATEGORY_LABELS],
          amount: category[1],
        }
      : null;
  }, [expenses]);

  const recent = [...expenses]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 4);

  return (
    <View className="gap-3">
      <View className="flex-row gap-2">
        <SummaryStat label="Total do mês" value={formatCurrency(total ?? 0)} tone="negative" />
        <SummaryStat
          label={topCategory ? `Maior: ${topCategory.label}` : 'Maior categoria'}
          value={topCategory ? formatCurrency(topCategory.amount) : '—'}
        />
      </View>

      {isLoading ? (
        <SummarySkeleton rows={3} />
      ) : recent.length ? (
        <View className="gap-2">
          {recent.map((expense) => (
            <SummaryRow
              key={expense.id}
              icon="wallet-outline"
              title={expense.description || 'Gasto'}
              description={`${
                expense.category ? EXPENSE_CATEGORY_LABELS[expense.category] : 'Outros'
              } · ${formatDate(expense.date)}`}
              value={formatCurrency(expense.amount)}
              valueTone="negative"
            />
          ))}
        </View>
      ) : (
        <SummaryEmpty message="Nenhum gasto lançado neste mês." />
      )}
    </View>
  );
}
